import { hasKnownDevice } from "./common";
import MultiCall from "../utils/multiCall";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { dateTimeComparator, dstCalculator, getDateTokenString } from "../utils/date";
import * as logger from "../utils/logger";
import { default as Api } from "../services/api";
import { getServerDate } from "../utils/storage/date";
import { IDailyOdometer, IDayOdometer } from "../model/DutyStatusLogs";
import { extend } from "../utils/vanilla";
import DutyStatusLog from "../model/DutyStatusLog";
import { DriveStore } from "../store/reducers";
import { DailyOdometerActions } from "../store/dailyOdometer/actions";
import { isStatusLog } from "hos-tools";
import { IApiError } from "../utils/api.interfaces";
import { getKnownDeviceId, getLogKnownDeviceId } from "./hosSyncHelpers";

const NO_USER_ID = "NoUserId";

export interface IStatusData {
    controller: string;
    data: number;
    dateTime: string;
    device: IIdEntity;
    diagnostic: IIdEntity;
}

type TDiagnosticId = "DiagnosticOdometerAdjustmentId" | "DiagnosticEngineHoursAdjustmentId";

const STATUS_DATA_CACHE_KEYS = {
    "DiagnosticEngineHoursAdjustmentId": "engineHours",
    "DiagnosticForLogAfterPlaceholder": "logAfterPlaceholderOdometer"
};

export class StatusDataCache {
    localLogs: IIdDictionary<DutyStatusLog>;
    private readonly statusData = {};
    private multiCall: MultiCall;
    private lastReceivedCurrentTimeData  = new Date(0);
    private isStatusDataOverLimit = false;

    constructor (
        private readonly api: Api,
        private readonly store: DriveStore
    ) {}

    addStatusDataToCache(localLogs: IIdDictionary<DutyStatusLog>) {
        Object.keys(localLogs).forEach(id => {
            let log = localLogs[id];
            let deviceId = getLogKnownDeviceId(log);

            if (deviceId && log.engineHours) {
                this.addStatusData(deviceId, log.dateTime, "engineHours", log.engineHours);
            }
        });
    }

    fetchStatusData(doubleRetentionStartDate: Date, localLogs: IIdDictionary<DutyStatusLog>): Promise<IIdDictionary<DutyStatusLog>> {
        this.addStatusDataToCache(localLogs);
        return new Promise<IIdDictionary<DutyStatusLog>>((resolve, reject) => {
            let lastDeviceId = "";
            let lastLogDateTime = (getServerDate()).toISOString();
            let logs = Object.keys(localLogs).reduce((result, logId) => {
                result.push(localLogs[logId]);
                return result;
            }, [] as DutyStatusLog[]).sort(dateTimeComparator());

            let retentionStartTime = doubleRetentionStartDate.getTime();

            this.multiCall = new MultiCall(this.api);
            this.isStatusDataOverLimit = false;

            // Decide which logs need StatusData
            for (let i = 0; i < logs.length; i++) {
                let log = logs[i];
                let deviceId = getLogKnownDeviceId(log);

                // No device on this log, or too old (outside driver's retention period)
                if (!deviceId || new Date(log.dateTime).getTime() < retentionStartTime) {
                    continue;
                }

                // Is missing engine data?
                !log.engineHours && log.engineHours !== 0 && this.addEngineHoursCall(deviceId, log.dateTime);

                // Run through the "standard" logs
                if (log.driver.id !== NO_USER_ID && isStatusLog(log)) {
                    // If device has switched, get last device status data
                    if (lastDeviceId && deviceId !== lastDeviceId) {
                        this.addEngineHoursCall(lastDeviceId, log.dateTime);
                    }
                    lastDeviceId = deviceId;
                }
            }

            // Get current engine hours records (needed for distance & compliance print)
            if (getKnownDeviceId(lastDeviceId) && (getServerDate().getTime() - this.lastReceivedCurrentTimeData.getTime() > (30 * 1000))) {
                this.addEngineHoursCall(lastDeviceId, lastLogDateTime);
                this.lastReceivedCurrentTimeData = getServerDate();
            }

            this.invokeMulticall(logs, lastLogDateTime)
                .then(() => resolve(localLogs))
                .catch(reject);
        });
    }

    populateStatusData (log: DutyStatusLog, force = false) {
        if (!log.engineHours || force) {
            log.engineHours = this.getEngineHours(log);
        }
    }

    invokeMulticall(logs: DutyStatusLog[], lastLogDateTime) {
        return new Promise((resolve) => {
            // eslint-disable-next-line complexity
            this.multiCall.beginInvoke().then(() => {
                let apiUser = this.api.getUser();
                let apiUserId  = apiUser ? apiUser.id : undefined;
                let dailyOdometer: IDailyOdometer = apiUserId ? extend(true, {}, this.store.getState().dailyOdometer[apiUserId] || {}) : {};
                let setDailyOdometer = (log, nextLog) => {
                    let logDate = getDateTokenString(dstCalculator.toLocalDateTime(log.dateTime));
                    let deviceId = hasKnownDevice(log) ? log.device.id : null;

                    if (deviceId) {
                        dailyOdometer[logDate] = dailyOdometer[logDate] || {};
                        dailyOdometer[logDate][deviceId] = dailyOdometer[logDate][deviceId] || {} as IDayOdometer;
                        let currentEngineHours = dailyOdometer[logDate][deviceId].endEngineHours;
                        dailyOdometer[logDate][deviceId].endEngineHours = currentEngineHours ? Math.max(currentEngineHours, nextLog.engineHours) : nextLog.engineHours;
                    }
                };

                for (let i = 1; i <= logs.length; i++) {
                    let prevLog = logs[i - 1];
                    let log: any = {
                        device: prevLog.device,
                        dateTime: logs[i] ? logs[i].dateTime : lastLogDateTime
                    };

                    this.populateStatusData(prevLog);
                    this.populateStatusData(log as DutyStatusLog, true);
                    setDailyOdometer(prevLog, log);
                }

                apiUserId && this.store.dispatch(DailyOdometerActions.setDailyOdometer({ userId: apiUserId, dailyOdometer: dailyOdometer }));
                resolve();
            }).catch((error: IApiError) => {
                const requestIndex = error?.data?.requestIndex;
                const call = requestIndex !== undefined ? this.multiCall.getFailedCall(requestIndex) : null;
                const search = call?.params?.search;
                const deviceId = search?.deviceSearch?.id ?? "Unknown device";
                const diagnosticId = search?.diagnosticSearch?.id ?? "Unknown diagnostic";
                const dateTime = search?.fromDate ?? "Unknown dateTime";

                logger.error(
                    logger.Source.SYNC,
                    `GetStatusDataCache Device Id: ${ deviceId } From/To Date: ${ dateTime } Diagnostic Id: ${ diagnosticId } error: ${ error?.message || "Unknown" }`,
                    this.api.getUser()?.id,
                    error
                );
                resolve();
            });
        });
    }

    private addStatusData(deviceId: string, dateTime: string, diagnosticId: string, value) {
        this.statusData[deviceId] = this.statusData[deviceId] || {};
        this.statusData[deviceId][dateTime] = this.statusData[deviceId][dateTime] || {};
        this.statusData[deviceId][dateTime][diagnosticId] = value;
    }

    private getStatusData(log: DutyStatusLog, diagnosticId: TDiagnosticId) {
        let cacheKey = STATUS_DATA_CACHE_KEYS[diagnosticId];
        let logAfterPlaceholderKey = STATUS_DATA_CACHE_KEYS["DiagnosticForLogAfterPlaceholder"];
        let deviceId = getLogKnownDeviceId(log);
        if (!deviceId) {
            return 0;
        }

        let dateTime = log.dateTime;
        let logStatusData = this.statusData[deviceId]
            && this.statusData[deviceId][dateTime]
            && (this.statusData[deviceId][dateTime][logAfterPlaceholderKey] || this.statusData[deviceId][dateTime][cacheKey]);

        return logStatusData || 0;
    }

    private addEngineHoursCall(deviceId: string | null, dateTime: string) {
        this.addStatusDataCall(deviceId, dateTime, "DiagnosticEngineHoursAdjustmentId");
    }

    private getEngineHours(log: DutyStatusLog) {
        return this.getStatusData(log, "DiagnosticEngineHoursAdjustmentId");
    }

    private addStatusDataCall(requestedDeviceId: string | null, dateTime: string, diagnosticId: TDiagnosticId) {
        // Check device.id is defined and not NoDeviceId
        let deviceId = getKnownDeviceId(requestedDeviceId);
        if (deviceId) {
            if (this.isStatusDataOverLimit || this.multiCall.getLength() >= 50) {
                if (!this.isStatusDataOverLimit) {
                    this.isStatusDataOverLimit = true;
                    logger.error(logger.Source.ERROR, "Status data multi-call count exceeded");
                }
                return;
            }

            let cacheKey = STATUS_DATA_CACHE_KEYS[diagnosticId];
            if (!this.statusData[deviceId] || !this.statusData[deviceId][dateTime] || !this.statusData[deviceId][dateTime][cacheKey]) {
                this.multiCall.add(
                    "Get",
                    {
                        typeName: "StatusData",
                        search: {
                            deviceSearch: { id: deviceId },
                            diagnosticSearch: { id: diagnosticId },
                            fromDate: dateTime,
                            toDate: dateTime
                        },
                        resultsLimit: 1,
                        propertySelector: {
                            fields: ["data"],
                            isIncluded: true
                        }
                    },
                    (result: IStatusData[]) => {
                        if (result && result[0] && deviceId) {
                            this.addStatusData(deviceId, dateTime, cacheKey, result[0].data);
                        } else {
                            logger.log(logger.Source.ERROR, `GetStatusDataCache Device Id: ${ deviceId } Diagnostic Id: ${ diagnosticId } From/To Date: ${ dateTime }`);
                        }
                    }
                );
            }
        }
    }
}